import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import { getAllProducts } from '@/lib/queries/products'
import type { ProductListItem } from '@/lib/queries/products'

/**
 * Products List page
 * Mobile-first design optimized for iPad Mini
 * Based on wireframe: products-list.html
 */
export default function ProductsList() {
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const [typeFilter, setTypeFilter] = useState<'all' | 'physical' | 'pod'>('all')
  const [channelFilter, setChannelFilter] = useState<string | null>(null)

  const { data: products, isLoading } = useQuery({
    queryKey: ['products'],
    queryFn: getAllProducts,
    staleTime: 0,
    gcTime: 0,
  })

  // Filter products based on search query and filters
  const filteredProducts = useMemo(() => {
    if (!products) return []

    let filtered = products

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim()
      filtered = filtered.filter((product) => {
        const titleMatch = product.title?.toLowerCase().includes(query)
        const skuMatch = product.sku?.toLowerCase().includes(query)
        return titleMatch || skuMatch
      })
    }

    // Type filter
    if (typeFilter === 'physical') {
      filtered = filtered.filter((product) => !product.isPrintOnDemand)
    } else if (typeFilter === 'pod') {
      filtered = filtered.filter((product) => product.isPrintOnDemand === true)
    }

    // Channel filter
    if (channelFilter) {
      filtered = filtered.filter((product) =>
        product.channelMapping?.some((mapping) => mapping.channel === channelFilter)
      )
    }

    return filtered
  }, [products, searchQuery, typeFilter, channelFilter])

  // Get unique channels from products
  const availableChannels = useMemo(() => {
    if (!products) return []
    const channels = new Set<string>()
    products.forEach((product) => {
      product.channelMapping?.forEach((mapping) => {
        // Filter out null/undefined channels
        if (mapping.channel) {
          channels.add(mapping.channel)
        }
      })
    })
    return Array.from(channels).sort()
  }, [products])

  // Helper to format price
  const formatPrice = (price: number | undefined): string => {
    if (price === undefined || price === null) return 'N/A'
    return `£${price.toFixed(2)}`
  }

  // Helper to get product image URL
  const getProductImageUrl = (product: ProductListItem): string | null => {
    // Try hero image first
    if (product.media?.hero?.url) {
      return product.media.hero.url
    }
    // Try first gallery image
    if (product.media?.galleryImages && product.media.galleryImages.length > 0) {
      return product.media.galleryImages[0].url || null
    }
    return null
  }

  // Helper to get channel display name
  const getChannelDisplayName = (channelCode: string | null | undefined): string => {
    if (!channelCode) return 'Unknown'
    // Capitalize first letter
    return channelCode.charAt(0).toUpperCase() + channelCode.slice(1)
  }

  // Helper to get bundle info text
  const getBundleInfo = (product: ProductListItem): string => {
    const parts: string[] = []
    if (product.relations?.relatedBundle?.title) {
      parts.push(`Bundle: ${product.relations.relatedBundle.title}`)
    }
    // TODO: Add custom set info when available
    // TODO: Add listings count when available
    return parts.join(' • ')
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 pb-24 flex items-center justify-center">
        <div className="text-gray-500">Loading products...</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <header className="sticky top-0 bg-white border-b border-gray-200 z-10">
        <div className="p-4">
          <div className="mb-3">
            <h1 className="text-2xl font-bold text-gray-900">
              Products
              {products && (
                <span className="text-lg font-normal text-gray-500 ml-2">
                  ({filteredProducts.length})
                </span>
              )}
            </h1>
          </div>
          <input
            type="search"
            placeholder="Search products..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-3 py-3 border border-gray-300 rounded-lg text-base min-h-[48px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          {/* Filter Chips */}
          <div className="flex gap-2 overflow-x-auto mt-3 pb-2 -mx-4 px-4">
            <button
              onClick={() => setTypeFilter('all')}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap touch-target-min-44 ${
                typeFilter === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setTypeFilter('physical')}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap touch-target-min-44 ${
                typeFilter === 'physical'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Physical
            </button>
            <button
              onClick={() => setTypeFilter('pod')}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap touch-target-min-44 ${
                typeFilter === 'pod'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              POD
            </button>
            {availableChannels.map((channel) => (
              <button
                key={channel}
                onClick={() => setChannelFilter(channelFilter === channel ? null : channel)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap touch-target-min-44 ${
                  channelFilter === channel
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {getChannelDisplayName(channel)}
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="p-4">
        {filteredProducts.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 mb-4">
              {searchQuery || typeFilter !== 'all' || channelFilter
                ? 'No products found matching your filters'
                : 'No products found'}
            </p>
            {!searchQuery && typeFilter === 'all' && !channelFilter && (
              <Link
                to="/products/new"
                className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg font-medium touch-target-min-44 hover:bg-blue-700 transition-colors"
              >
                Create Your First Product
              </Link>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {filteredProducts.map((product) => {
              const imageUrl = getProductImageUrl(product)
              const channel = product.channelMapping?.[0]?.channel
              const bundleInfo = getBundleInfo(product)

              return (
                <div
                  key={product._id}
                  onClick={() => navigate(`/products/${product._id}`)}
                  className="block bg-white rounded-lg p-4 shadow-sm border border-gray-200 hover:shadow-md transition-shadow touch-target-min-44 cursor-pointer"
                >
                  {/* Product Header */}
                  <div className="flex gap-3 mb-3">
                    {/* Product Image */}
                    <div className="w-20 h-20 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {imageUrl ? (
                        <img
                          src={imageUrl}
                          alt={product.title || 'Product'}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span className="text-3xl">🖼️</span>
                      )}
                    </div>

                    {/* Product Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-semibold text-gray-900 mb-1 truncate">
                        {product.title || 'Untitled Product'}
                      </h3>
                      <p className="text-xs text-gray-600 mb-1">SKU: {product.sku}</p>
                      <div className="flex gap-2 flex-wrap mb-2">
                        {channel && (
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium">
                            {getChannelDisplayName(channel)}
                          </span>
                        )}
                        {product.isPrintOnDemand ? (
                          <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs font-medium">
                            POD
                          </span>
                        ) : (
                          <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium">
                            Physical
                          </span>
                        )}
                      </div>
                      <div className="text-xl font-bold text-blue-600">
                        {formatPrice(product.rrp)}
                      </div>
                    </div>
                  </div>

                  {/* Bundle Info */}
                  {bundleInfo && (
                    <div className="text-xs text-gray-600 mb-3">{bundleInfo}</div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-2 mt-3" onClick={(e) => e.stopPropagation()}>
                    <Link
                      to={`/listings/new?productId=${product._id}&step=3`}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-sm text-center bg-white hover:bg-gray-50 transition-colors touch-target-min-44"
                    >
                      Create Listing
                    </Link>
                    <Link
                      to={`/products/${product._id}`}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-sm text-center bg-white hover:bg-gray-50 transition-colors touch-target-min-44"
                    >
                      View
                    </Link>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </main>

      {/* Floating Action Button */}
      <Link
        to="/products/new"
        className="fixed bottom-24 right-5 w-14 h-14 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-light shadow-lg hover:bg-blue-700 transition-colors touch-target-min-44 z-50"
        aria-label="Create new product"
      >
        +
      </Link>
    </div>
  )
}
